import type { ActionResult, AppState, FeedbackResult, PushPublicKeyResult } from "./types";

const PROFILE_ID_KEY = "yatta_profile_id";
const CLIENT_SECRET_KEY = "yatta_client_secret";
const DISPLAY_NAME_KEY = "yatta_display_name";

interface ProfileResponse {
  profileId: string;
  clientSecret?: string;
}

function getHeaders(): HeadersInit {
  const profileId = localStorage.getItem(PROFILE_ID_KEY);
  const clientSecret = localStorage.getItem(CLIENT_SECRET_KEY);
  return {
    "content-type": "application/json",
    ...(profileId ? { "x-profile-id": profileId } : {}),
    ...(clientSecret ? { "x-client-secret": clientSecret } : {}),
  };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...getHeaders(),
      ...init.headers,
    },
  });

  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error ?? `Request failed: ${response.status}`);
  }
  return payload;
}

export async function ensureProfile(): Promise<void> {
  const displayName = getStoredDisplayName();
  const profile = await request<ProfileResponse>("/api/profile", {
    method: "POST",
    body: JSON.stringify({ displayName }),
  });
  localStorage.setItem(PROFILE_ID_KEY, profile.profileId);
  if (profile.clientSecret) {
    localStorage.setItem(CLIENT_SECRET_KEY, profile.clientSecret);
  }
}

export function getStoredDisplayName(): string {
  return localStorage.getItem(DISPLAY_NAME_KEY)?.trim() ?? "";
}

export function saveDisplayName(name: string): string {
  const clean = name.trim().replace(/\s+/g, " ").slice(0, 24);
  localStorage.setItem(DISPLAY_NAME_KEY, clean);
  return clean;
}

export async function getState(): Promise<AppState> {
  return request<AppState>("/api/state");
}

export async function createTeam(): Promise<AppState> {
  return request<AppState>("/api/team", { method: "POST", body: "{}" });
}

export async function joinTeam(inviteCode: string): Promise<AppState> {
  return request<AppState>("/api/team/join", {
    method: "POST",
    body: JSON.stringify({ inviteCode }),
  });
}

export async function pressYatta(): Promise<ActionResult> {
  return request<ActionResult>("/api/yatta", { method: "POST", body: "{}" });
}

export async function useMakeup(): Promise<ActionResult> {
  return request<ActionResult>("/api/makeup", { method: "POST", body: "{}" });
}

export async function sendFeedback(body: string): Promise<FeedbackResult> {
  return request<FeedbackResult>("/api/feedback", {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export async function getPushPublicKey(): Promise<PushPublicKeyResult> {
  return request<PushPublicKeyResult>("/api/push/public-key");
}

export async function subscribePush(subscription: PushSubscriptionJSON): Promise<{ success: boolean }> {
  return request<{ success: boolean }>("/api/push/subscribe", {
    method: "POST",
    body: JSON.stringify(subscription),
  });
}

export async function unsubscribePush(endpoint: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>("/api/push/unsubscribe", {
    method: "POST",
    body: JSON.stringify({ endpoint }),
  });
}
